import apiUrl from '../components/Url';

const STORE = 'bulka';

const SESSION_ID_KEY = '__asid';
const VISIT_ATTR_KEY = '__aattr';
const FIRST_TOUCH_KEY = '__aftattr';
const PRICE_GROUP_KEY = '__apg';

function uuid() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
}

function getSessionId() {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = uuid();
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

/**
 * The visitor's A/B price group, 'A' or 'B', picked at random on the first visit and kept
 * in localStorage — so refreshing, new tabs and later visits all show the same prices.
 * Shared by every running price test.
 */
export function getPriceGroup() {
  try {
    let group = localStorage.getItem(PRICE_GROUP_KEY);
    if (group !== 'A' && group !== 'B') {
      group = Math.random() < 0.5 ? 'A' : 'B';
      localStorage.setItem(PRICE_GROUP_KEY, group);
    }
    return group;
  } catch (_) {
    return 'A'; // no storage: a stable group is impossible, so show the control price
  }
}

function readJson(storage, key) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function detectDevice() {
  const w = window.innerWidth || 1024;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

/**
 * Attribution for the current visit.
 *
 * Captured once, on the session's first hit, because UTM params live only on the landing
 * URL — the moment the SPA router navigates, they are gone from the address bar and can
 * never be recovered. Everything downstream (events, and the order at checkout) reads
 * this snapshot rather than the live URL.
 */
export function getVisitAttribution() {
  const existing = readJson(sessionStorage, VISIT_ATTR_KEY);
  if (existing) return existing;

  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (_) {
    params = new URLSearchParams('');
  }

  const clean = v => {
    const s = (v || '').trim();
    return s === '' ? null : s.slice(0, 150);
  };

  const attr = {
    utm_source: clean(params.get('utm_source')),
    utm_medium: clean(params.get('utm_medium')),
    utm_campaign: clean(params.get('utm_campaign')),
    utm_content: clean(params.get('utm_content')),
    utm_term: clean(params.get('utm_term')),
    referrer: document.referrer || null,
    landing_page: window.location.pathname,
  };

  try {
    sessionStorage.setItem(VISIT_ATTR_KEY, JSON.stringify(attr));
  } catch (_) {}

  rememberFirstTouch(attr);
  return attr;
}

function hasSignal(attr) {
  return Boolean(attr && (attr.utm_source || attr.utm_campaign || attr.utm_medium || attr.referrer));
}

/**
 * First-touch attribution, persisted across visits — this is what an order is credited to.
 *
 * A stored first touch is never overwritten by a later campaign, with one exception: if
 * the stored one is direct (no campaign, no referrer) it carries no information, and
 * leaving it in place would permanently deny credit to the ad that actually brought the
 * customer back. So a direct first touch is upgraded by the first real one that arrives.
 */
function rememberFirstTouch(attr) {
  try {
    const stored = readJson(localStorage, FIRST_TOUCH_KEY);
    if (stored && hasSignal(stored)) return;
    if (!stored || hasSignal(attr)) {
      localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(attr));
    }
  } catch (_) {}
}

/**
 * The attribution to send with an order. Falls back to the current visit when no first
 * touch was ever stored (e.g. localStorage unavailable).
 */
export function getOrderAttribution() {
  const visit = getVisitAttribution();
  const first = readJson(localStorage, FIRST_TOUCH_KEY);
  const base = (first && hasSignal(first)) ? first : visit;

  return { ...base, session_id: getSessionId(), price_group: getPriceGroup() };
}

export function trackEvent(event, payload = {}) {
  try {
    const attr = getVisitAttribution();

    const body = JSON.stringify({
      event,
      store: STORE,
      session_id: getSessionId(),
      page_path: window.location.pathname,
      country: localStorage.getItem('country') || null,
      referrer: attr.referrer,
      utm_source: attr.utm_source,
      utm_medium: attr.utm_medium,
      utm_campaign: attr.utm_campaign,
      utm_content: attr.utm_content,
      utm_term: attr.utm_term,
      landing_page: attr.landing_page,
      device: detectDevice(),
      price_group: getPriceGroup(),
      ...payload,
    });

    fetch(`${apiUrl}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (_) {}
}
